import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { TERMS_AND_CONDITIONS } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const TermsAndConditionsPage = ({ setRefs }) => {
  const termsAndConditionsRef = useRef(null);
  const additionalTermsRef = useRef(null);
  const additionalTermsAdditionalParagraphRef = useRef(null);
  const changesToTermsRef = useRef(null);
  const changesToSiteRef = useRef(null);
  const suspensionRef = useRef(null);
  const transferOfRightsRef = useRef(null);
  const geographicLimitationsRef = useRef(null);
  const accountSecurityRef = useRef(null);
  const contentUsageRef = useRef(null);
  const noDataMiningRef = useRef(null);
  const linkingToSiteRef = useRef(null);
  const noRelianceRef = useRef(null);
  const externalLinksRef = useRef(null);
  const virusProtectionRef = useRef(null);
  const liabilityLimitationsRef = useRef(null);
  const personalInfoUsageRef = useRef(null);
  const governingLawRef = useRef(null);

  const refs = useMemo(
    () => ({
      termsAndConditions: termsAndConditionsRef,
      additionalTerms: additionalTermsRef,
      additionalTermsAdditionalParagraph: additionalTermsAdditionalParagraphRef,
      changesToTerms: changesToTermsRef,
      changesToSite: changesToSiteRef,
      suspension: suspensionRef,
      transferOfRights: transferOfRightsRef,
      geographicLimitations: geographicLimitationsRef,
      accountSecurity: accountSecurityRef,
      contentUsage: contentUsageRef,
      noDataMining: noDataMiningRef,
      linkingToSite: linkingToSiteRef,
      noReliance: noRelianceRef,
      externalLinks: externalLinksRef,
      virusProtection: virusProtectionRef,
      liabilityLimitations: liabilityLimitationsRef,
      personalInfoUsage: personalInfoUsageRef,
      governingLaw: governingLawRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.termsOfUse.title}
        content={seoData.termsOfUse.content}
        keywords={seoData.termsOfUse.keywords}
        canonical={seoData.termsOfUse.canonical}
        ogImage={seoData.termsOfUse.ogImage}
      />
      {/* Page Header */}
      <PageHeader
        id='hero'
        title={TERMS_AND_CONDITIONS.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />

      {/* Introduction */}
      <SectionContent
        id='termsAndConditions'
        ref={refs.termsAndConditions}
        initialSpacing={false}
        text={[
          {
            text: TERMS_AND_CONDITIONS.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          {
            text: TERMS_AND_CONDITIONS.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Additional Terms section */}
      <SectionContent
        id='additionalTerms'
        ref={refs.additionalTerms}
        title={TERMS_AND_CONDITIONS.SECTIONS.ADDITIONAL_TERMS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.ADDITIONAL_TERMS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={TERMS_AND_CONDITIONS.SECTIONS.ADDITIONAL_TERMS.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        initialSpacing={false}
        ref={refs.additionalTermsAdditionalParagraph}
        text={[
          {
            text: TERMS_AND_CONDITIONS.SECTIONS.ADDITIONAL_TERMS
              .ADDITIONAL_PARAGRAPH,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Changes to These Terms section */}
      <SectionContent
        id='changesToTerms'
        ref={refs.changesToTerms}
        title={TERMS_AND_CONDITIONS.SECTIONS.CHANGES_TO_TERMS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.CHANGES_TO_TERMS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Changes to Our Site section */}
      <SectionContent
        id='changesToSite'
        ref={refs.changesToSite}
        title={TERMS_AND_CONDITIONS.SECTIONS.CHANGES_TO_SITE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.CHANGES_TO_SITE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Suspension or Withdrawal of Our Site section */}
      <SectionContent
        id='suspension'
        ref={refs.suspension}
        title={TERMS_AND_CONDITIONS.SECTIONS.SUSPENSION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.SUSPENSION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Transfer of Rights and Obligations section */}
      <SectionContent
        id='transferOfRights'
        ref={refs.transferOfRights}
        title={TERMS_AND_CONDITIONS.SECTIONS.TRANSFER_OF_RIGHTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.TRANSFER_OF_RIGHTS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Geographic Limitations section */}
      <SectionContent
        id='geographicLimitations'
        ref={refs.geographicLimitations}
        title={TERMS_AND_CONDITIONS.SECTIONS.GEOGRAPHIC_LIMITATIONS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.GEOGRAPHIC_LIMITATIONS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Account Security section */}
      <SectionContent
        id='accountSecurity'
        ref={refs.accountSecurity}
        title={TERMS_AND_CONDITIONS.SECTIONS.ACCOUNT_SECURITY.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.ACCOUNT_SECURITY.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Usage of Content on Our Site section */}
      <SectionContent
        id='contentUsage'
        ref={refs.contentUsage}
        title={TERMS_AND_CONDITIONS.SECTIONS.CONTENT_USAGE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.CONTENT_USAGE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* No Data Mining or Web Scraping section */}
      <SectionContent
        id='noDataMining'
        ref={refs.noDataMining}
        title={TERMS_AND_CONDITIONS.SECTIONS.NO_DATA_MINING.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.NO_DATA_MINING.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Linking to Our Site section */}
      <SectionContent
        id='linkingToSite'
        ref={refs.linkingToSite}
        title={TERMS_AND_CONDITIONS.SECTIONS.LINKING_TO_SITE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={TERMS_AND_CONDITIONS.SECTIONS.LINKING_TO_SITE.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          })
        )}
        text={TERMS_AND_CONDITIONS.SECTIONS.LINKING_TO_SITE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* No Reliance on Site Content section */}
      <SectionContent
        id='noReliance'
        ref={refs.noReliance}
        title={TERMS_AND_CONDITIONS.SECTIONS.NO_RELIANCE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.NO_RELIANCE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* External Links section */}
      <SectionContent
        id='externalLinks'
        ref={refs.externalLinks}
        title={TERMS_AND_CONDITIONS.SECTIONS.EXTERNAL_LINKS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.EXTERNAL_LINKS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Protection Against Viruses section */}
      <SectionContent
        id='virusProtection'
        ref={refs.virusProtection}
        title={TERMS_AND_CONDITIONS.SECTIONS.VIRUS_PROTECTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.VIRUS_PROTECTION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Liability Limitations section */}
      <SectionContent
        id='liabilityLimitations'
        ref={refs.liabilityLimitations}
        title={TERMS_AND_CONDITIONS.SECTIONS.LIABILITY_LIMITATIONS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={TERMS_AND_CONDITIONS.SECTIONS.LIABILITY_LIMITATIONS.LIST_ITEMS}
        />
      </Box>

      {/* How We Use Your Personal Information section */}
      <SectionContent
        id='personalInfoUsage'
        ref={refs.personalInfoUsage}
        title={TERMS_AND_CONDITIONS.SECTIONS.PERSONAL_INFO_USAGE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.PERSONAL_INFO_USAGE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Governing Law and Dispute Resolution section */}
      <SectionContent
        id='governingLaw'
        ref={refs.governingLaw}
        title={TERMS_AND_CONDITIONS.SECTIONS.GOVERNING_LAW.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={TERMS_AND_CONDITIONS.SECTIONS.GOVERNING_LAW.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {TERMS_AND_CONDITIONS.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default TermsAndConditionsPage;
